import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DataContext } from '../Components/DataContext';
import NotFoundPage from './NotFoundPage';
import SEOHead from '../Components/SEOHead';

const CustomPage = () => {
  const { slug } = useParams();
  const data = useContext(DataContext);
  
  // Get slug from URL path if useParams doesn't work
  const currentSlug = slug || window.location.pathname.replace('/', '');


  // Get page data from localStorage first, then fallback to data.json
  const getPageData = () => {
    const savedData = localStorage.getItem('websiteData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        if (parsedData.customPages && parsedData.customPages[currentSlug]) {
          return parsedData.customPages[currentSlug];
        }
      } catch (error) {
        console.error('Error parsing saved data:', error);
      }
    }
    
    // Fallback to data.json
    if (data && data.customPages && data.customPages[currentSlug]) {
      return data.customPages[currentSlug];
    }
    
    return null;
  };

  const pageData = getPageData();

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!pageData) {
    return <NotFoundPage />;
  }

  // Generate SEO metadata based on page type
  const getSEOMetadata = () => {
    if (currentSlug === 'legal') {
      return {
        title: "Privacy Policy & Terms of Service - Drosh The Pod",
        description: "Privacy policy and terms of service for Drosh The Pod podiatry services. Learn about our data protection practices and service terms.",
        keywords: "privacy policy, terms of service, data protection, GDPR, podiatry terms, medical privacy",
        canonical: "https://www.droshpod.com/legal"
      };
    }
    
    return {
      title: `${pageData.name} - Drosh The Pod`,
      description: `Learn more about ${pageData.name} at Drosh The Pod professional podiatry services.`,
      keywords: "podiatry, foot care, manchester, professional services",
      canonical: `https://www.droshpod.com/${currentSlug}`
    };
  };

  const seoData = getSEOMetadata();

  const renderBlock = (block) => {
    switch (block.type) {
      case 'heading':
        const HeadingTag = block.content.level;
        return (
          <HeadingTag key={block.id} style={block.styles}>
            {block.content.text}
          </HeadingTag>
        );
      
      case 'paragraph':
        return (
          <p key={block.id} style={block.styles}>
            {block.content.text}
          </p>
        );
      
      case 'image':
        return (
          <div key={block.id} style={block.styles}>
            <img
              src={block.content.src || 'https://via.placeholder.com/400x300'}
              alt={block.content.alt}
              className="w-full h-auto rounded-lg"
            />
            {block.content.caption && (
              <p className="text-sm text-gray-600 mt-2 text-center">
                {block.content.caption}
              </p>
            )}
          </div>
        );
      
      case 'button':
        return (
          <div key={block.id} style={block.styles} className="text-center">
            <button
              className={`px-6 py-3 rounded-lg font-medium ${
                block.content.style === 'primary' 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => block.content.link && window.open(block.content.link, '_blank')}
            >
              {block.content.text}
            </button>
          </div>
        );
      
      case 'list':
        const ListTag = block.content.type === 'ol' ? 'ol' : 'ul';
        return (
          <ListTag key={block.id} style={block.styles} className={block.content.type === 'ol' ? 'list-decimal list-inside' : 'list-disc list-inside'}>
            {block.content.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ListTag>
        );
      
      case 'quote':
        return (
          <blockquote key={block.id} style={block.styles} className="border-l-4 border-blue-500 pl-4 italic">
            <p>"{block.content.text}"</p>
            {block.content.author && (
              <footer className="text-sm text-gray-600 mt-2">
                — {block.content.author}
              </footer>
            )}
          </blockquote>
        );
      
      case 'video':
        return (
          <div key={block.id} style={block.styles}>
            <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
              {block.content.src ? (
                <video controls className="w-full h-full rounded-lg">
                  <source src={block.content.src} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="text-center text-gray-500">
                  <div className="w-12 h-12 mx-auto mb-2 bg-gray-300 rounded flex items-center justify-center">
                    📹
                  </div>
                  <p>No video source</p>
                </div>
              )}
            </div>
            {block.content.title && (
              <h3 className="text-lg font-semibold mt-2">{block.content.title}</h3>
            )}
            {block.content.description && (
              <p className="text-gray-600 mt-1">{block.content.description}</p>
            )}
          </div>
        );
      
      case 'code':
        return (
          <pre key={block.id} style={block.styles} className="bg-gray-100 p-4 rounded-lg overflow-x-auto">
            <code>{block.content.code}</code>
          </pre>
        );
      
      default:
        return <div key={block.id}>Unknown block type: {block.type}</div>;
    }
  };

  return (
    <>
      <SEOHead 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
      />
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-lg shadow-lg p-8"
        >
          {pageData.blocks && pageData.blocks.length > 0 ? (
            pageData.blocks.map((block, index) => (
              <motion.div
                key={block.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                {renderBlock(block)}
              </motion.div>
            ))
          ) : (
            <div className="text-center py-12">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{pageData.name}</h1>
              <p className="text-gray-600">This page is currently being updated. Please check back soon.</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default CustomPage;
